import React, { Suspense, useCallback, useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "next-themes";
import { toast } from "sonner";
import {
  SignOut,
  ShieldCheck,
  ClockCounterClockwise,
  Gear,
  Buildings,
  FilePdf,
  MagnifyingGlass,
  ArrowsLeftRight,
  House,
  Calculator,
  Funnel,
  Table,
  Sparkle,
  List,
  X,
  Bell,
  Sun,
  Moon,
  Monitor,
  Crown,
  Camera,
  ListChecks,
  Columns,
} from "@phosphor-icons/react";

import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: House, roles: ["admin", "agent", "accountant"] },
  { to: "/commission-grids", label: "Commission Grids", icon: FilePdf, roles: ["admin", "accountant"] },
  { to: "/image-scans", label: "Image Scans", icon: Camera, roles: ["admin", "accountant"] },
  { to: "/column-maps", label: "Column Maps", icon: Columns, roles: ["admin", "accountant"] },
  { to: "/scorecard", label: "Scorecard", icon: ListChecks, roles: ["admin"] },
  { to: "/grid-explorer", label: "Grid Explorer", icon: MagnifyingGlass, roles: ["admin", "agent", "accountant"] },
  { to: "/rate-cards", label: "Rate Cards", icon: Table, roles: ["admin", "agent", "accountant"] },
  { to: "/grid-finder", label: "Quick Grid Finder", icon: Funnel, roles: ["admin", "agent", "accountant", "sales"] },
  { to: "/reconciliation", label: "Reconciliation", icon: ArrowsLeftRight, roles: ["admin", "accountant"] },
  { to: "/reconcile-check", label: "Quick Reconcile", icon: Calculator, roles: ["admin", "accountant", "sales"] },
  { to: "/audit", label: "Audit Logs", icon: ClockCounterClockwise, roles: ["admin", "accountant"] },
  { to: "/prompts", label: "Prompt Manager", icon: Sparkle, roles: ["admin"] },
  { to: "/organization", label: "Organization", icon: Buildings, roles: ["admin"] },
  { to: "/settings", label: "Settings", icon: Gear, roles: ["admin", "agent", "accountant", "sales"] },
  { to: "/superadmin", label: "Superadmin", icon: Crown, roles: ["superadmin"] },
];

// Roles allowed to hit /api/renewals/upcoming (mirrors the /renewals route)
const RENEWAL_ROLES = ["admin", "agent", "accountant"];

/** Layout-stable skeleton shown while a lazy route chunk downloads — mirrors
 *  the PageHeader + table shape so the page landing causes no layout shift. */
function PageFallback() {
  return (
    <div aria-busy="true" aria-label="Loading page" data-testid="page-fallback">
      <div className="mb-8">
        <Skeleton className="h-3 w-24 mb-3" />
        <Skeleton className="h-9 w-64 mb-3" />
        <Skeleton className="h-4 w-96 max-w-full" />
      </div>
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    </div>
  );
}

function NavItem({ item, onNavigate }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end
      onClick={onNavigate}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        [
          "group flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
          isActive
            ? "bg-primary text-primary-foreground"
            : "text-foreground/80 hover:bg-secondary hover:text-foreground",
        ].join(" ")
      }
    >
      <Icon size={18} weight="duotone" />
      <span>{item.label}</span>
    </NavLink>
  );
}

/** Icon-only nav item for the tablet rail (md–lg). */
function RailItem({ item }) {
  const Icon = item.icon;
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <NavLink
          to={item.to}
          end
          data-testid={`rail-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          aria-label={item.label}
          className={({ isActive }) =>
            [
              "flex items-center justify-center h-10 w-10 rounded-md transition-colors",
              isActive
                ? "bg-primary text-primary-foreground"
                : "text-foreground/70 hover:bg-secondary hover:text-foreground",
            ].join(" ")
          }
        >
          <Icon size={20} weight="duotone" />
        </NavLink>
      </TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
}

/** Shared sidebar body — used by both the desktop rail and the mobile drawer. */
function SidebarBody({ user, initials, visibleNav, onLogout, onNavigate }) {
  return (
    <>
      <div className="px-5 pt-6 pb-5">
        <NavLink to="/dashboard" onClick={onNavigate} className="flex items-center gap-2 group">
          <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
            <ShieldCheck size={20} weight="duotone" />
          </div>
          <div>
            <div className="font-display text-lg leading-none group-hover:text-primary transition-colors">
              Broker OS
            </div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mt-1">
              Insurance Mgmt
            </div>
          </div>
        </NavLink>
      </div>
      <Separator />
      <nav className="px-3 py-4 flex flex-col gap-1 flex-1 overflow-y-auto">
        {visibleNav.map((item) => (
          <NavItem key={item.to} item={item} onNavigate={onNavigate} />
        ))}
      </nav>
      <Separator />
      <div className="p-3 flex items-center gap-3">
        <Avatar className="h-9 w-9 border border-border">
          <AvatarFallback className="bg-secondary text-foreground text-xs font-medium">
            {initials}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate" data-testid="current-user-name">
            {user?.name}
          </div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
            {user?.role}
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onLogout}
          data-testid="logout-button"
          aria-label="Logout"
        >
          <SignOut size={18} />
        </Button>
      </div>
    </>
  );
}

/** Global search — command palette over role-visible pages + quick actions. */
function CommandMenu({ open, setOpen, visibleNav, onLogout }) {
  const navigate = useNavigate();
  const { setTheme } = useTheme();

  const run = useCallback(
    (fn) => {
      setOpen(false);
      fn();
    },
    [setOpen],
  );

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search pages and actions…" data-testid="command-search-input" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup heading="Pages">
          {visibleNav.map((item) => {
            const Icon = item.icon;
            return (
              <CommandItem
                key={item.to}
                value={item.label}
                onSelect={() => run(() => navigate(item.to))}
                data-testid={`command-item-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon size={16} weight="duotone" className="mr-2" />
                {item.label}
              </CommandItem>
            );
          })}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Appearance">
          <CommandItem value="light theme" onSelect={() => run(() => setTheme("light"))}>
            <Sun size={16} className="mr-2" /> Light theme
          </CommandItem>
          <CommandItem value="dark theme" onSelect={() => run(() => setTheme("dark"))}>
            <Moon size={16} className="mr-2" /> Dark theme
          </CommandItem>
          <CommandItem value="system theme" onSelect={() => run(() => setTheme("system"))}>
            <Monitor size={16} className="mr-2" /> System theme
          </CommandItem>
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Account">
          <CommandItem value="settings" onSelect={() => run(() => navigate("/settings"))}>
            <Gear size={16} className="mr-2" /> Settings
          </CommandItem>
          <CommandItem value="logout sign out" onSelect={() => run(onLogout)}>
            <SignOut size={16} className="mr-2" /> Log out
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}

/** Upcoming-renewals bell with count badge (roles with /renewals access). */
function NotificationsBell({ role }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const allowed = RENEWAL_ROLES.includes(role);

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    api
      .get("/renewals/upcoming", { params: { days: 30 } })
      .then(({ data }) => {
        if (!cancelled) setItems(data.items || []);
      })
      .catch(() => {}); // bell is best-effort — never block the shell
    return () => {
      cancelled = true;
    };
  }, [allowed]);

  if (!allowed) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label="Notifications"
          data-testid="notifications-bell"
        >
          <Bell size={20} weight="duotone" />
          {items.length > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-0.5 rounded-full bg-accent text-accent-foreground text-[10px] font-semibold flex items-center justify-center">
              {items.length > 99 ? "99+" : items.length}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Renewals due — next 30 days</span>
          {items.length > 0 && <Badge variant="secondary">{items.length}</Badge>}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 ? (
          <div className="px-2 py-6 text-center text-sm text-muted-foreground">
            Nothing due in the next 30 days
          </div>
        ) : (
          items.slice(0, 5).map((p) => (
            <DropdownMenuItem
              key={p.id}
              className="flex flex-col items-start gap-0.5"
              onClick={() => navigate("/renewals")}
            >
              <span className="text-sm font-medium truncate w-full">
                {p.customer_name || p.policy_number}
              </span>
              <span className="text-xs text-muted-foreground">
                {p.policy_number} · expires {p.expiry_date}
                {typeof p.days_to_expiry === "number" ? ` (${p.days_to_expiry}d)` : ""}
              </span>
            </DropdownMenuItem>
          ))
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="justify-center text-sm font-medium text-primary"
          onClick={() => navigate("/renewals")}
          data-testid="notifications-view-all"
        >
          View all renewals
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Change theme" data-testid="theme-toggle">
          <Sun size={20} weight="duotone" className="dark:hidden" />
          <Moon size={20} weight="duotone" className="hidden dark:block" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => setTheme("light")} data-testid="theme-light">
          <Sun size={16} className="mr-2" /> Light {theme === "light" && "✓"}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme("dark")} data-testid="theme-dark">
          <Moon size={16} className="mr-2" /> Dark {theme === "dark" && "✓"}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme("system")} data-testid="theme-system">
          <Monitor size={16} className="mr-2" /> System {theme === "system" && "✓"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu({ user, initials, onLogout }) {
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Account menu"
          data-testid="user-menu-button"
        >
          <Avatar className="h-8 w-8 border border-border">
            <AvatarFallback className="bg-secondary text-foreground text-xs font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="text-sm font-medium truncate">{user?.name}</div>
          <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="user-menu-settings">
          <Gear size={16} className="mr-2" /> Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onLogout} data-testid="user-menu-logout">
          <SignOut size={16} className="mr-2" /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Network status — warn when offline, confirm recovery. Data-entry users
  // lose work silently otherwise.
  useEffect(() => {
    const onOffline = () =>
      toast.warning("You're offline", {
        id: "net-status",
        description: "Changes can't be saved until the connection returns.",
        duration: Infinity,
      });
    const onOnline = () =>
      toast.success("Back online", { id: "net-status", duration: 2500 });
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
    };
  }, []);

  const initials = (user?.name || "U")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const visibleNav = NAV_ITEMS.filter(
    (i) => i.roles.includes(user?.role) || user?.role === "superadmin",
  );

  // Global ⌘K / Ctrl+K opens the command palette
  useEffect(() => {
    function onKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  async function handleLogout() {
    setMobileOpen(false);
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background flex">
        {/* Desktop sidebar (lg+) */}
        <aside
          className="hidden lg:flex w-64 flex-col border-r border-border bg-card sticky top-0 h-screen"
          data-testid="sidebar"
        >
          <SidebarBody
            user={user}
            initials={initials}
            visibleNav={visibleNav}
            onLogout={handleLogout}
          />
        </aside>

        {/* Tablet icon rail (md–lg) */}
        <aside
          className="hidden md:flex lg:hidden w-16 flex-col items-center border-r border-border bg-card sticky top-0 h-screen py-4 gap-1"
          data-testid="tablet-rail"
        >
          <NavLink
            to="/dashboard"
            aria-label="Broker OS home"
            className="h-10 w-10 mb-2 rounded-md bg-primary text-primary-foreground flex items-center justify-center"
          >
            <ShieldCheck size={20} weight="duotone" />
          </NavLink>
          <Separator className="mb-2" />
          <nav className="flex flex-col gap-1 flex-1 overflow-y-auto">
            {visibleNav.map((item) => (
              <RailItem key={item.to} item={item} />
            ))}
          </nav>
        </aside>

        {/* Mobile drawer backdrop */}
        <div
          className={[
            "md:hidden fixed inset-0 z-40 bg-black/50 transition-opacity duration-200",
            mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none",
          ].join(" ")}
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />

        {/* Mobile drawer — slides in from the LEFT */}
        <aside
          className={[
            "md:hidden fixed top-0 left-0 z-50 w-64 h-screen flex flex-col bg-card border-r border-border",
            "transition-transform duration-200 ease-out",
            mobileOpen ? "translate-x-0" : "-translate-x-full",
          ].join(" ")}
          data-testid="mobile-sidebar"
        >
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
            data-testid="mobile-menu-close"
            className="absolute top-2 right-2 z-10"
          >
            <X size={20} />
          </Button>
          <SidebarBody
            user={user}
            initials={initials}
            visibleNav={visibleNav}
            onLogout={handleLogout}
            onNavigate={() => setMobileOpen(false)}
          />
        </aside>

        {/* Main column: top bar + content */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Top bar — solid surface, 1px border, no glassmorphism */}
          <header
            className="sticky top-0 z-30 h-14 bg-card border-b border-border flex items-center gap-2 px-3 sm:px-4"
            data-testid="topbar"
          >
            {/* Mobile: hamburger + logo */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-button"
            >
              <List size={22} />
            </Button>
            <NavLink to="/dashboard" className="md:hidden flex items-center gap-2 mr-1">
              <div className="h-7 w-7 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
                <ShieldCheck size={16} weight="duotone" />
              </div>
              <span className="font-display text-sm">Broker OS</span>
            </NavLink>

            {/* Global search trigger */}
            <button
              onClick={() => setSearchOpen(true)}
              data-testid="global-search-button"
              className="flex items-center gap-2 h-9 rounded-md border border-input bg-background px-3 text-sm text-muted-foreground hover:bg-secondary transition-colors md:w-72 ml-auto md:ml-0"
              aria-label="Search"
            >
              <MagnifyingGlass size={16} />
              <span className="hidden md:inline">Search pages…</span>
              <kbd className="hidden md:inline-flex ml-auto pointer-events-none h-5 select-none items-center gap-1 rounded border border-border bg-muted px-1.5 text-[10px] font-medium text-muted-foreground">
                Ctrl K
              </kbd>
            </button>

            <div className="hidden md:block flex-1" />

            <NotificationsBell role={user?.role} />
            <ThemeToggle />
            <UserMenu user={user} initials={initials} onLogout={handleLogout} />
          </header>

          <main className="flex-1 min-w-0">
            {/* key on pathname re-runs the enter animation per navigation;
                the boundary also resets so a crashed page can be re-tried
                by navigating away and back. */}
            <div
              key={location.pathname}
              className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-10 animate-page-in"
            >
              <ErrorBoundary>
                <Suspense fallback={<PageFallback />}>
                  <Outlet />
                </Suspense>
              </ErrorBoundary>
            </div>
          </main>
        </div>

        <CommandMenu
          open={searchOpen}
          setOpen={setSearchOpen}
          visibleNav={visibleNav}
          onLogout={handleLogout}
        />
      </div>
    </TooltipProvider>
  );
}
